// MoneyHQ building blocks on top of shadcn/ui: brand mark, stock initials, sparkline, range bars,
// coloured changes and the feed status badge.
import { useId, useRef, useState, type ReactNode } from 'react';
import { usePixelSnap } from '@/lib/pixel-snap';
import { cn } from '@/lib/utils';
import { fmtPct } from '@/charts/board';
import type { FeedStatus, Mode } from '@/lib/client/use-market';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { apiUrl } from '@/lib/api';
import { LOGOS, logoPath } from '@/lib/logos';
import { HolidayTag } from './holiday-tag';

/**
 * The wordmark's box for a height in px, chosen so the coin (the "o", 65 units across, its top-left at
 * 130,62 in the artwork) is a whole number of pixels across and starts on a whole pixel: scaled freely
 * its edge fell between pixels and blurred.
 */
function logoBox(size: number) {
  const d = Math.max(8, Math.round((size * 65) / 114)); // the coin, in px
  const k = d / 65; // px per artwork unit
  const left = Math.ceil((130 - 29) * k), top = Math.ceil((62 - 41) * k);
  const x = 130 - left / k, y = 62 - top / k;
  const h = Math.max(size, Math.ceil((152 - y) * k)), w = Math.ceil((548 - x) * k);
  return { width: w, height: h, viewBox: `${x} ${y} ${w / k} ${h / k}` };
}

/** The MoneyHQ wordmark, all in the primary blue: "Money" with a coin as its "o" (the AI sparkle cut
 * out of it, so it shows the page behind), then "HQ" (public/brand/moneyhq-wordmark.svg). `size` is
 * its height in px. */
export function Logo({ size = 28, className }: { size?: number; className?: string }) {
  const ref = useRef<SVGSVGElement>(null);
  usePixelSnap(ref);
  return (
    <svg ref={ref} role="img" aria-label="MoneyHQ" data-mark="logo" {...logoBox(size)} className={className}>
      <path d="M31.0795 45.5455H52.3182L74.75 100.273H75.7045L98.1364 45.5455H119.375V127H102.67V73.983H101.994L80.9148 126.602H69.5398L48.4602 73.7841H47.7841V127H31.0795V45.5455Z" fill="#0496FF" />
      <path d="M221.705 91.6818V127H204.761V65.9091H220.909V76.6875H221.625C222.977 73.1345 225.244 70.3239 228.426 68.2557C231.608 66.161 235.466 65.1136 240 65.1136C244.242 65.1136 247.941 66.0417 251.097 67.8977C254.252 69.7538 256.705 72.4053 258.455 75.8523C260.205 79.2727 261.08 83.3561 261.08 88.1023V127H244.136V91.125C244.163 87.3864 243.208 84.4697 241.273 82.375C239.337 80.2538 236.672 79.1932 233.278 79.1932C230.998 79.1932 228.983 79.6837 227.233 80.6648C225.509 81.6458 224.157 83.0777 223.176 84.9602C222.222 86.8163 221.731 89.0568 221.705 91.6818ZM297.794 128.193C291.51 128.193 286.101 126.92 281.567 124.375C277.059 121.803 273.586 118.17 271.146 113.477C268.707 108.758 267.487 103.176 267.487 96.733C267.487 90.4489 268.707 84.9337 271.146 80.1875C273.586 75.4413 277.02 71.7424 281.448 69.0909C285.902 66.4394 291.126 65.1136 297.118 65.1136C301.148 65.1136 304.9 65.7633 308.374 67.0625C311.874 68.3352 314.923 70.2576 317.521 72.8295C320.146 75.4015 322.188 78.6364 323.646 82.5341C325.105 86.4053 325.834 90.9394 325.834 96.1364V100.79H274.249V90.2898H309.885C309.885 87.8504 309.355 85.6894 308.294 83.8068C307.234 81.9242 305.762 80.4526 303.879 79.392C302.023 78.3049 299.862 77.7614 297.396 77.7614C294.824 77.7614 292.544 78.358 290.556 79.5511C288.593 80.7178 287.056 82.2955 285.942 84.2841C284.828 86.2462 284.258 88.4337 284.232 90.8466V100.83C284.232 103.852 284.788 106.464 285.902 108.665C287.042 110.866 288.646 112.562 290.715 113.756C292.783 114.949 295.235 115.545 298.073 115.545C299.955 115.545 301.679 115.28 303.243 114.75C304.807 114.22 306.146 113.424 307.26 112.364C308.374 111.303 309.222 110.004 309.806 108.466L325.476 109.5C324.681 113.265 323.05 116.553 320.584 119.364C318.145 122.148 314.989 124.322 311.118 125.886C307.273 127.424 302.832 128.193 297.794 128.193ZM341.23 149.909C339.083 149.909 337.067 149.737 335.185 149.392C333.329 149.074 331.791 148.663 330.571 148.159L334.389 135.511C336.378 136.121 338.168 136.453 339.759 136.506C341.376 136.559 342.768 136.188 343.935 135.392C345.128 134.597 346.096 133.244 346.838 131.335L347.833 128.75L325.918 65.9091H343.736L356.384 110.773H357.02L369.787 65.9091H387.725L363.98 133.602C362.84 136.89 361.289 139.754 359.327 142.193C357.391 144.659 354.939 146.555 351.969 147.881C348.999 149.233 345.42 149.909 341.23 149.909ZM392.233 127V45.5455H409.455V79.1534H444.415V45.5455H461.597V127H444.415V93.3523H409.455V127H392.233ZM502.06 98.6818H516.696L524.054 108.148L531.293 116.58L544.935 133.682H528.867L519.481 122.148L514.668 115.307L502.06 98.6818ZM546.168 86.2727C546.168 95.1553 544.484 102.712 541.117 108.943C537.776 115.174 533.215 119.934 527.435 123.222C521.681 126.483 515.212 128.114 508.026 128.114C500.787 128.114 494.291 126.47 488.537 123.182C482.784 119.894 478.236 115.134 474.895 108.903C471.554 102.672 469.884 95.1288 469.884 86.2727C469.884 77.3902 471.554 69.8333 474.895 63.6023C478.236 57.3712 482.784 52.625 488.537 49.3636C494.291 46.0758 500.787 44.4318 508.026 44.4318C515.212 44.4318 521.681 46.0758 527.435 49.3636C533.215 52.625 537.776 57.3712 541.117 63.6023C544.484 69.8333 546.168 77.3902 546.168 86.2727ZM528.708 86.2727C528.708 80.5189 527.846 75.6667 526.123 71.7159C524.426 67.7652 522.026 64.7689 518.924 62.7273C515.821 60.6856 512.189 59.6648 508.026 59.6648C503.863 59.6648 500.231 60.6856 497.128 62.7273C494.026 64.7689 491.613 67.7652 489.89 71.7159C488.193 75.6667 487.344 80.5189 487.344 86.2727C487.344 92.0265 488.193 96.8788 489.89 100.83C491.613 104.78 494.026 107.777 497.128 109.818C500.231 111.86 503.863 112.881 508.026 112.881C512.189 112.881 515.821 111.86 518.924 109.818C522.026 107.777 524.426 104.78 526.123 100.83C527.846 96.8788 528.708 92.0265 528.708 86.2727Z" fill="#0496FF" />
      <path d="M162.5 62C180.449 62 195 76.5507 195 94.5C195 112.449 180.449 127 162.5 127C144.551 127 130 112.449 130 94.5C130 76.5507 144.551 62 162.5 62ZM162.014 74.8857C160.571 81.0039 158.799 85.0452 155.922 87.9219C153.045 90.7986 149.004 92.5711 142.886 94.0137V94.9863C149.004 96.4289 153.045 98.2014 155.922 101.078C158.799 103.955 160.571 107.996 162.014 114.114H162.986C164.429 107.996 166.201 103.955 169.078 101.078C171.955 98.2014 175.996 96.4289 182.114 94.9863V94.0137C175.996 92.5711 171.955 90.7986 169.078 87.9219C166.201 85.0452 164.429 81.0039 162.986 74.8857H162.014Z" fill="#0496FF" />
    </svg>
  );
}

/** The coin alone (the collapsed sidebar, phones): drawn on the pixel grid like DeskIcon, in the brand blue. */
export function CoinMark({ size = 28 }: { size?: number }) {
  return <PixelCoin px={size} className="text-primary" mark="coin" />;
}

/** The desk's (the AI's) icon: the logo's coin with its sparkle cut out, in the text colour. Drawn on
 * the pixel grid of the size it is shown at (from its size-* class: 14, 16, 18 or 20 px), the coin's
 * edge on whole pixels, so it stays sharp; scaling the logo's artwork down would blur it. */
const COIN_STAR = 'M162.014 74.8857C160.571 81.0039 158.799 85.0452 155.922 87.9219C153.045 90.7986 149.004 92.5711 142.886 94.0137V94.9863C149.004 96.4289 153.045 98.2014 155.922 101.078C158.799 103.955 160.571 107.996 162.014 114.114H162.986C164.429 107.996 166.201 103.955 169.078 101.078C171.955 98.2014 175.996 96.4289 182.114 94.9863V94.0137C175.996 92.5711 171.955 90.7986 169.078 87.9219C166.201 85.0452 164.429 81.0039 162.986 74.8857H162.014Z';
const ICON_PX: Record<string, number> = { 'size-3': 12, 'size-3.5': 14, 'size-4': 16, 'size-[18px]': 18, 'size-5': 20, 'size-6': 24, 'size-8': 32, 'size-14': 56 };
export function DeskIcon({ className }: { className?: string }) {
  const px = ICON_PX[(className ?? '').split(/\s+/).find((c) => c in ICON_PX) ?? ''] ?? 16;
  return <PixelCoin px={px} className={className} mark="desk" />;
}

/** The coin with its sparkle cut out, px across, its edge on whole pixels (1 px in from each side). */
function PixelCoin({ px, className, mark }: { px: number; className?: string; mark: string }) {
  const id = `coin-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const ref = useRef<SVGSVGElement>(null);
  usePixelSnap(ref);
  const c = px / 2, r = c - 1;
  return (
    <svg ref={ref} viewBox={`0 0 ${px} ${px}`} width={px} height={px} aria-hidden="true" data-mark={mark} className={cn('shrink-0', className)}>
      <mask id={id}>
        <circle cx={c} cy={c} r={r} fill="#fff" />
        <path d={COIN_STAR} fill="#000" transform={`translate(${c} ${c}) scale(${r / 32.5}) translate(-162.5 -94.5)`} />
      </mask>
      <rect width={px} height={px} fill="currentColor" mask={`url(#${id})`} />
    </svg>
  );
}

const PALETTE = ['#0070c9', '#0f766e', '#cc2b39', '#1d4ed8', '#b45309', '#4d7c0f', '#7e22ce', '#0369a1', '#9d174d', '#15803d'];
const SIZES = { sm: 'size-8 rounded-lg text-[11px]', md: 'size-10 rounded-[10px] text-[13px]', lg: 'size-13 rounded-xl text-[17px]' };

/** The company's logo, a finished square (scripts/logos: full-colour squares as they are, other
 *  marks centred on white), filling the tile; coloured initials,
 *  stable per symbol, for a stock without one or when the image fails. */
export function Initials({ symbol, size = 'md' }: { symbol: string; size?: keyof typeof SIZES }) {
  const [broken, setBroken] = useState(false);
  if (LOGOS.has(symbol) && !broken) {
    return (
      // No background behind the image: at the rounded corners it would show through the clipped
      // edge as a light rim (the logos carry their own white where they need it).
      <span className={cn('grid flex-none place-items-center ring-1 ring-border/60', SIZES[size])} aria-hidden="true">
        <img src={logoPath(symbol)} alt="" loading="lazy" decoding="async" draggable={false}
          className="size-full rounded-[inherit] object-cover" onError={() => setBroken(true)} />
      </span>
    );
  }
  let h = 0;
  for (const ch of symbol) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return (
    <span className={cn('grid flex-none place-items-center font-heading font-semibold text-white', SIZES[size])}
      style={{ background: PALETTE[h % PALETTE.length] }} aria-hidden="true">
      {symbol.replace(/[^A-Z]/g, '').slice(0, 2)}
    </span>
  );
}

/** Tiny intraday line from a % series. */
export function Sparkline({ series, up, width = 96, height = 28 }: { series: number[]; up: boolean; width?: number; height?: number }) {
  if (series.length < 2) return <svg width={width} height={height} />;
  const lo = Math.min(...series), hi = Math.max(...series), span = hi - lo || 1;
  const y = (v: number) => height - 3 - ((v - lo) / span) * (height - 6);
  const pts = series.map((v, i) => `${((i / (series.length - 1)) * width).toFixed(1)},${y(v).toFixed(1)}`);
  const zero = y(0);
  return (
    <svg width={width} height={height} aria-hidden="true" className="block">
      {zero > 0 && zero < height && <line x1={0} x2={width} y1={zero} y2={zero} stroke="#e4e4e7" strokeDasharray="2 2" />}
      <polyline points={pts.join(' ')} fill="none" stroke={up ? 'var(--bull)' : 'var(--bear)'} strokeWidth={1.4} />
    </svg>
  );
}

/** Low → high bar with the current value marked (Groww's day range). */
export function RangeBar({ lo, hi, value, loLabel = 'L', hiLabel = 'H', gradient }: {
  lo: number; hi: number; value: number; loLabel?: ReactNode; hiLabel?: ReactNode; gradient?: boolean;
}) {
  const pos = hi > lo ? Math.max(0, Math.min(100, ((value - lo) / (hi - lo)) * 100)) : 50;
  return (
    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2.5 text-xs text-muted-foreground">
      <span className="num">{loLabel}</span>
      <span className={cn('relative h-1 rounded-full bg-border',
        gradient && 'h-1.5 bg-[linear-gradient(90deg,var(--bear),#fbd3d6_45%,#c6eee2_55%,var(--bull))]')}>
        <i className="absolute -top-1 h-3 w-0.5 -translate-x-px rounded-sm bg-ink" style={{ left: `${pos}%` }} />
      </span>
      <span className="num">{hiLabel}</span>
    </div>
  );
}

/** A % change in market colours. */
export function Change({ pct, className }: { pct: number | null; className?: string }) {
  if (pct === null) return <span className={cn('num text-muted-foreground', className)}>—</span>;
  return <span className={cn('num', pct >= 0 ? 'text-bull' : 'text-bear', className)}>{fmtPct(pct)}</span>;
}

/** Bullish / bearish tag. */
export function SideTag({ bull, className, children }: { bull: boolean; className?: string; children?: ReactNode }) {
  return (
    <span className={cn('inline-flex h-5 items-center rounded-full px-2 text-[10.5px] font-semibold tracking-wide uppercase',
      bull ? 'bg-bull-soft text-bull-strong' : 'bg-bear-soft text-bear-strong', className)}>
      {children ?? (bull ? 'Bullish' : 'Bearish')}
    </span>
  );
}

/** 0–100 strength as a short bar plus the number. */
export function StrengthBar({ score, bull }: { score: number; bull: boolean }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-1.5 w-14 overflow-hidden rounded-full bg-muted">
        <span className={cn('block h-full rounded-full', bull ? 'bg-bull' : 'bg-bear')} style={{ width: `${Math.max(4, score)}%` }} />
      </span>
      <span className="num w-6 text-right text-[13px] text-ink">{score}</span>
    </span>
  );
}

const MODE_STYLES = {
  sim: 'border-warn-line bg-warn-soft text-warn-ink',
  load: 'border-brand-line bg-brand-soft text-brand-strong',
  live: 'border-bull-line bg-bull-soft text-bull-strong',
  err: 'border-bear-line bg-bear-soft text-bear-strong',
  closed: 'border-border bg-muted text-muted-foreground',
};

/** Where the numbers come from: simulated, loading, live or error, with the reason on hover. */
export function ModeBadge({ mode, status }: { mode: Mode; status: FeedStatus | null }) {
  // A holiday: "Holiday ⓘ", the holiday, when it opens and the session shown in the tooltip.
  if (mode === 'closed' && status?.clock?.state === 'holiday') {
    return (
      <span data-mode="holiday" className={cn('inline-flex h-7 items-center rounded-full border px-3 text-[11px] font-semibold', MODE_STYLES.closed)}>
        <HolidayTag clock={status.clock} session={status.date} />
      </span>
    );
  }
  let kind: keyof typeof MODE_STYLES = 'sim', text = 'Simulated', title = 'Prices are simulated until the live feed is connected.';
  if (mode === 'sim' && status) {
    if (status.locked) { text = 'Practice · simulated'; title = 'A realistic simulated session for practice; the live board opens once your access is approved.'; }
    else if (status.offline) { text = 'Simulated · feed offline'; title = status.error ?? 'The feed service is not reachable.'; }
    else if (status.brokerConfigured === false) { text = 'Simulated · no data feed'; title = 'Live market data isn’t configured on the server.'; }
    else if (!status.loggedIn) { text = 'Simulated · data offline'; title = 'The live market data feed isn’t connected right now.'; }
  } else if (mode === 'loading') {
    kind = 'load';
    text = status?.phase === 'backfill' ? `Loading ${status.done}/${status.total}` : 'Loading…';
    title = 'Loading today’s session.';
  } else if (mode === 'live') {
    kind = 'live';
    text = status?.ticker === 'connected' ? 'Live' : 'Live · reconnecting';
    title = `Last tick ${status?.lastTick ?? '—'}`;
  } else if (mode === 'closed') {
    kind = 'live'; text = 'Market closed';
    title = status?.loggedIn ? `Showing the ${status?.date ?? 'last'} session.` : `Showing the last session (${status?.date ?? 'latest'}); live data starts when the broker connects.`;
  } else if (mode === 'error') {
    kind = 'err';
    text = status?.offline ? 'Can’t reach MoneyHQ' : 'Data error';
    title = status?.offline ? (status.error ?? 'The MoneyHQ server isn’t reachable right now.') : 'The market data feed reported an error.';
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span data-mode={mode} className={cn('inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-[11px] font-semibold whitespace-nowrap', MODE_STYLES[kind])}>
          {kind === 'live' && <span className={cn('size-1.5 rounded-full bg-bull', mode === 'live' && 'animate-pulse')} />}
          {text}
        </span>
      </TooltipTrigger>
      {title && <TooltipContent>{title}</TooltipContent>}
    </Tooltip>
  );
}

export function KiteLogin({ status }: { status: FeedStatus | null }) {
  if (!status || status.offline || !status.brokerConfigured || status.loggedIn) return null;
  return (
    <a href={apiUrl('/v1/kite/login')} className="kite-login inline-flex h-8 items-center rounded-full bg-orange-500 px-3 text-xs font-semibold text-white hover:bg-orange-600">
      <span className="sm:hidden">Kite login</span><span className="hidden sm:inline">Login with Kite</span>
    </a>
  );
}

/** Label-over-value stat. */
export function Stat({ label, value, className }: { label: ReactNode; value: ReactNode; className?: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('num text-[15px] font-medium text-ink', className)}>{value}</div>
    </div>
  );
}

/** Label-left, value-right row. */
export function KV({ label, value, className }: { label: ReactNode; value: ReactNode; className?: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-border/60 py-2.5 text-[13px] last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('num text-right font-medium text-ink', className)}>{value}</span>
    </div>
  );
}

/** Single-choice pill chips (Groww's filter chips), built on shadcn's ToggleGroup. */
export function Pills<T extends string>({ options, value, onChange, label, size = 'md', className }: {
  options: { id: T; label: ReactNode }[]; value: T; onChange: (v: T) => void; label: string; size?: 'sm' | 'md'; className?: string;
}) {
  return (
    <ToggleGroup type="single" value={value} onValueChange={(v) => v && onChange(v as T)} aria-label={label} spacing={size === 'sm' ? 1 : 2}
      className={cn('flex-wrap', className)}>
      {options.map((o) => (
        <ToggleGroupItem key={o.id} value={o.id}
          className={cn('rounded-full border bg-card font-medium text-foreground hover:border-primary/60 hover:bg-card',
            'data-[state=on]:border-primary data-[state=on]:bg-brand-soft data-[state=on]:text-brand-strong',
            size === 'sm' ? 'h-7 min-w-0 px-2.5 text-xs' : 'h-8 px-3.5 text-[13px]')}>
          {o.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
